/**
 * The "Use a template" page: loads the template bundle (definition + pre-fill) for the routed document
 * type and hosts the two-pane TemplateEditor. This shell owns the API: drafting calls the backend LLM
 * endpoint, and Create sends the answers to the backend — the AUTHORITATIVE resolver — which renders the
 * .docx onto the checklist slot; on success the checklist is refreshed and the user lands back on the
 * document page with the finished file open in the editor.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useActiveCompany } from '../state';
import { TemplateEditor } from '../components/TemplateEditor';
import {
  draftTemplateSection,
  generateTemplateDocument,
  getTemplateDefinition,
  type TemplateAnswers,
  type TemplateBundle,
} from '../../lib/entities/templates';
import '../../styles/app-templates.css';

export default function TemplateFillPage() {
  const { slug = '' } = useParams();
  const [search] = useSearchParams();
  const employeeId = search.get('employeeId') ?? undefined;
  const navigate = useNavigate();
  const { companyId, refetchChecklist, refetchDocuments } = useActiveCompany();

  const [bundle, setBundle] = useState<TemplateBundle | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!companyId || !slug) return;
    let live = true;
    setBundle(null);
    setLoadError(null);
    getTemplateDefinition(companyId, slug, employeeId)
      .then((b) => {
        if (live) setBundle(b);
      })
      .catch((err: unknown) => {
        if (live) setLoadError(err instanceof Error ? err.message : 'Could not load this template.');
      });
    return () => {
      live = false;
    };
  }, [companyId, slug, employeeId]);

  const onDraft = useCallback(
    (draftId: string, scalars: Record<string, string>) => {
      if (!companyId) return Promise.reject(new Error('No active company.'));
      return draftTemplateSection(companyId, slug, draftId, scalars);
    },
    [companyId, slug],
  );

  const onCreate = useCallback(
    async (answers: TemplateAnswers) => {
      if (!companyId) throw new Error('No active company.');
      await generateTemplateDocument(companyId, slug, answers, { employeeId });
      await Promise.all([refetchChecklist(), refetchDocuments()]);
      navigate(`/app/documents/${slug}`);
    },
    [companyId, slug, employeeId, refetchChecklist, refetchDocuments, navigate],
  );

  if (loadError) {
    return (
      <div className="apage tplf">
        <p className="tplf-status">{loadError}</p>
        <Link to={`/app/documents/${slug}`} className="btn btn-secondary btn-sm">
          Back to the document
        </Link>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="apage tplf">
        <p className="tplf-status">Loading the template…</p>
      </div>
    );
  }

  return (
    // Keyed by slug+scope: the editor seeds its answers ONCE per mount, so a different template (or a
    // different employee scope) must remount rather than update in place with stale answers.
    <TemplateEditor
      key={`${slug}:${employeeId ?? ''}`}
      bundle={bundle}
      backHref={`/app/documents/${slug}`}
      onDraft={onDraft}
      onCreate={onCreate}
    />
  );
}
